import React, { useRef, useCallback } from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import ProjectTemplateMenuPopup from '../../../components/ProjectTemplateMenuPopup';
import ExpandableSectionV2 from '../../../components/ExpandableSection/ExpandableSectionV2';
import { useProjectTemplateState } from './hooks/useProjectTemplateState';
import { getProjectTemplateName, stopEventPropagation } from './utils/templateUtils';
import TemplateVersionList from './components/TemplateVersionList';
import HoverActions from './components/HoverActions';

import pencilIcon from '../../../assets/images/pencil-icon-white.svg';
import trashIcon from '../../../assets/images/trash-icon.svg';
import './ProjectTemplateCard.scss';

const ProjectTemplateCard = ({
  projectTemplateGroup,
  selectedProjectTemplate: initialSelectedTemplate,
  projectType,
  currentUser,
  currentCompany,
  animateExpand,
  handleDeleteProjectTemplate,
  handleUpdateProjectTypeTemplates,
  handleEditTemplateName,
  handleAddCopiedTask,
  history,
}) => {
  const cardRef = useRef(null);

  // Use custom hook for state management
  const {
    showVersions,
    selectedProjectTemplate,
    hoveredOver,
    pencilHoveredOver,
    menuOpen,
    updateSelectedTemplate,
    getMostRecentTemplateVersion,
    handleCardClick,
    handleHoverIn,
    handleHoverOut,
    handlePencilHoverIn,
    handlePencilHoverOut,
    handlePencilClick,
    handleCloseMenu,
  } = useProjectTemplateState({
    selectedProjectTemplate: initialSelectedTemplate,
    projectTemplateGroup,
    animateExpand,
  });

  // Event handlers
  const handleDeleteTemplate = useCallback((e) => {
    stopEventPropagation(e);
    if (projectTemplateGroup[0]) {
      handleDeleteProjectTemplate(
        projectTemplateGroup[0].id, 
        projectTemplateGroup[0].group_identifier, 
        true
      );
    }
  }, [projectTemplateGroup, handleDeleteProjectTemplate]);

  const handleDeleteVersion = useCallback((template) => {
    if (projectTemplateGroup[0]) {
      handleDeleteProjectTemplate(
        projectTemplateGroup[0].id, 
        projectTemplateGroup[0].group_identifier, 
        false
      );
    }
  }, [projectTemplateGroup, handleDeleteProjectTemplate]);

  const handleTemplateSelected = useCallback((e, template) => {
    stopEventPropagation(e);
    updateSelectedTemplate(template);
    history.push(`/project-types/${projectType.id}/project-template/${template.id}`);
  }, [updateSelectedTemplate, history, projectType.id]);

  // Computed values

  const sortedTemplateGroups = _.sortBy(projectTemplateGroup, 'version');
  const projectTemplateName = getProjectTemplateName(sortedTemplateGroups, showVersions);
  const mostRecentTemplate = sortedTemplateGroups[sortedTemplateGroups.length - 1];

  // Render popup menu
  const renderPopupMenu = () => {
    if (!menuOpen || !cardRef.current || !mostRecentTemplate) return null;

    const rects = cardRef.current.getClientRects();
    return (
      <ProjectTemplateMenuPopup
        projectTemplate={mostRecentTemplate}
        cardRect={rects[0]}
        handleClosePopup={handleCloseMenu}
        handleTaskDeleted={() => {}} // Add proper handler if needed
        handleAddCopiedTask={handleAddCopiedTask}
        currentUser={currentUser}
        currentCompany={currentCompany}
        handleEditTemplateName={handleEditTemplateName}
        handleUpdateProjectTypeTemplates={handleUpdateProjectTypeTemplates}
      />
    );
  };

  return (
    <div 
      ref={cardRef}
      className="project-template-card" 
      onMouseEnter={() => handleHoverIn(currentUser)} 
      onMouseMove={() => handleHoverIn(currentUser)} 
      onMouseLeave={handleHoverOut}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      {/* Hover actions (pencil and delete icons) */}
      <HoverActions
        hoveredOver={hoveredOver}
        pencilHoveredOver={pencilHoveredOver}
        onPencilClick={handlePencilClick}
        onDeleteTemplate={handleDeleteTemplate}
        onPencilHoverIn={handlePencilHoverIn}
        onPencilHoverOut={handlePencilHoverOut}
        pencilIcon={pencilIcon}
        trashIcon={trashIcon}
      />

      {/* Popup menu */}
      {renderPopupMenu()}

      {/* Expandable section with template versions */}
      <ExpandableSectionV2
        key="project-template"
        animateExpand={animateExpand}
        expanded={showVersions}
        name={projectTemplateName}
        onExpand={handleCardClick} // Use the same handler as card click
        isSubSection={true}
        useExpandIcon={true}
        hovered={pencilHoveredOver || hoveredOver}
      >
        <TemplateVersionList
          projectTemplateGroup={projectTemplateGroup}
          selectedProjectTemplate={selectedProjectTemplate}
          hoveredOver={hoveredOver}
          onTemplateSelected={handleTemplateSelected}
          onDeleteVersion={handleDeleteVersion}
        />
      </ExpandableSectionV2>
    </div>
  );
};

ProjectTemplateCard.propTypes = {
  projectTemplateGroup: PropTypes.array.isRequired,
  selectedProjectTemplate: PropTypes.object,
  projectType: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  currentCompany: PropTypes.object.isRequired,
  animateExpand: PropTypes.bool,
  handleDeleteProjectTemplate: PropTypes.func.isRequired,
  handleEditTemplateName: PropTypes.func.isRequired,
  handleAddCopiedTask: PropTypes.func,
  history: PropTypes.object.isRequired,
};

export default withRouter(ProjectTemplateCard);



// WEBPACK FOOTER //
// ./src/containers/ProjectTypesContainer/ProjectTemplateCard/index.js